import { useMemo } from 'react';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';

export interface CustomerFilter {
  label: string;
  value: string[];
}

export interface ConsolidatedHistoryFiltersResponse {
  customers: CustomerFilter[];
  customerMakerIdsMap: Map<string, Set<number>>;
  divisionToOrgUuidMap: Map<number, string>;
  makerOrganizationUuidsFilter: string[];
}

interface ConsolidatedHistoryFiltersParams {
  makerIdsFilter: number[];
}

// Private function to transform maker IDs to organization UUIDs
const transformMakerIdsToUuids = (makerIds: number[], divisionToOrgUuidMap: Map<number, string>): string[] => {
  // Check if any makerIds are numeric (division IDs) that need transformation
  const hasNumericMakerIds = makerIds.some((id) => {
    return typeof id === 'number' && divisionToOrgUuidMap.has(id);
  });

  if (!hasNumericMakerIds) {
    // All IDs are already UUIDs, no transformation needed
    return [];
  }

  const transformedIds: string[] = [];
  for (const id of makerIds) {
    if (typeof id === 'number') {
      if (divisionToOrgUuidMap.has(id)) {
        transformedIds.push(divisionToOrgUuidMap.get(id)!);
      }
      // Skip unmapped numeric IDs
    } else {
      // If it's already a UUID, add as-is
      transformedIds.push(String(id));
    }
  }

  return transformedIds;
};

const useConsolidatedHistoryFilters = ({
  makerIdsFilter,
}: ConsolidatedHistoryFiltersParams): ConsolidatedHistoryFiltersResponse => {
  const { data: customersWithLookup } = useTakerMarkets((data) => {
    const groupedByName = new Map<string, Set<string>>();
    const customerMakerIdsMap = new Map<string, Set<number>>();
    const divisionToOrgUuidMap = new Map<number, string>();

    data.forEach(({ makerDivisionId, makerOrganizationName, makerOrganizationUuid }) => {
      if (!groupedByName.has(makerOrganizationName)) {
        groupedByName.set(makerOrganizationName, new Set());
        customerMakerIdsMap.set(makerOrganizationName, new Set());
      }

      // Store division ID for CSV download
      customerMakerIdsMap.get(makerOrganizationName)?.add(makerDivisionId);

      // Build mapping from division ID to organization UUID for transformation
      divisionToOrgUuidMap.set(makerDivisionId, makerOrganizationUuid);

      // Add appropriate ID to customer filter
      groupedByName.get(makerOrganizationName)?.add(makerOrganizationUuid);
    });

    const customers = Array.from(groupedByName.entries())
      .map(([label, value]) => ({
        label,
        value: Array.from(value),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return { customers, customerMakerIdsMap, divisionToOrgUuidMap };
  }, true); // always include inactive markets

  // Transform makerIds when makerIds are numeric division IDs from URL
  // Convert legacy division IDs to organization UUIDs
  const makerOrganizationUuidsFilter = useMemo(
    () => transformMakerIdsToUuids(makerIdsFilter, customersWithLookup?.divisionToOrgUuidMap),
    [makerIdsFilter, customersWithLookup?.divisionToOrgUuidMap]
  );

  return {
    customers: customersWithLookup?.customers || [],
    customerMakerIdsMap: customersWithLookup?.customerMakerIdsMap || new Map(),
    divisionToOrgUuidMap: customersWithLookup?.divisionToOrgUuidMap || new Map(),
    makerOrganizationUuidsFilter,
  };
};

export default useConsolidatedHistoryFilters;
